import {useEffect} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {useSelector} from 'react-redux'

import {prefetchConfig} from './prefetch.config'
import {reactQueryFetchFunctionMap} from './prefetch.queryfn'
import {selectUser} from 'features/User'
import {AdditionalQueryClientParamsType} from 'interfaces'
import {checkPermission, usePermissions} from 'rbac'

/**
 * @description A Hook to prefetch data on app mount
 * @param {condition} condition to prefetch
 * @returns void
 */
const useGlobalPrefetch = (condition?: boolean) => {
  const queryClient = useQueryClient()
  const {authProvider} = useSelector(selectUser)
  const {permissions: userPermissions} = usePermissions()

  const additionalQueryClientParams: AdditionalQueryClientParamsType = {
    getCASBOverview: {platform: (authProvider ?? '').toLowerCase()},
    getCASBFiles: {platform: (authProvider ?? '').toLowerCase()},
  }

  useEffect(() => {
    /**
     * If condition is not met, return
     * For example, if user is not logged in, we don't want to prefetch
     */
    if (!condition) return

    // Global prefetch
    prefetchConfig.global.forEach(pageConfig => {
      const {queries, isReactQuery} = pageConfig
      if (isReactQuery)
        queries.forEach(queryConfig => {
          const {queryKey, queryFn, reqPermissions} = queryConfig
          const data = queryClient.getQueryData(queryKey)
          if (!data) {
            const fetchFunction = reactQueryFetchFunctionMap[queryFn]
            const params = additionalQueryClientParams[queryKey[0]]

            const hasPermission = checkPermission(
              reqPermissions,
              userPermissions,
            )
            if (!hasPermission) return

            if (params) {
              fetchFunction(queryClient, params)
            } else {
              fetchFunction(queryClient)
            }
          }
        })
    })

    // ...future expansion for other scenarios
  }, [queryClient, condition, userPermissions])
}

export default useGlobalPrefetch
